import React, {FormEvent, useContext, useMemo, useRef, useState} from "react";
import {createInvestmentManager, createOrdersManager} from "../../../../di";
import {useNavigateByName, useProfileData} from "../../../../hooks";
import {DetailedProjectContext} from "../DetailedProjectContext/DetailedProjectContext";
import './ProjectInvest.scoped.scss';
import {Button, Checkbox, Information, InputMoney, Money, PrimaryButton, Tooltip} from "../../../common";
import {PrimaryButtonColor} from "../../../common/buttons/decorators/PrimaryButton/PrimaryButton";
import {Money as MoneyModel, TransactionType} from "../../../../api-client";
import {cn, Environment, resultIf, RouteDictionary} from "../../../../utils";
import ProjectProgress from "../../projects/ProjectProgress";
import {MoneyMode} from "../../../common/utils/Money/Money";
import {calculateCollectedPercentage} from "../../../../utils/pages";
import apiClientConfig from "../../../../configs/app";
import {useParams} from "react-router-dom";

const ProjectInvest = () => {
  const project = useContext(DetailedProjectContext).project;

  const maxInvestment = new MoneyModel(project.accounting.loan.amount - project.accounting.investedSum.amount, "RUB");
  const minInvestmentLimit = maxInvestment.amount < project.accounting.minInvestment.amount
    ? maxInvestment.amount
    : project.accounting.minInvestment.amount;
  const [money, setMoney] = useState<MoneyModel>(new MoneyModel(minInvestmentLimit, "RUB"));
  const profileData = useProfileData();
  const [loading, setLoading] = useState(false);
  const [isUnderstandRisks, setIsUnderstandRisks] = useState(false);
  const [isComplyRestriction, setIsComplyRestriction] = useState(false);
  const focused = useRef<boolean>(false);
  const {confirmationUuid} = useParams();
  const navigate = useNavigateByName();
  const isNoQualified = !profileData.isQualified;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const investmentFee = useMemo((): MoneyModel => {

    const settings = project.feeSettings.find(item => item.transactionType === TransactionType.INVESTMENT_FEE);

    if (settings) {
      if (settings.percent) {
        return new MoneyModel(money.amount * settings.percent / 100, "RUB");
      } else if (settings.money) {
        return settings.money;
      }
    }
    return new MoneyModel(0, "RUB");
  }, [money.amount]);


  const devAndTest = (apiClientConfig.applicationEnvironment === Environment.dev)
            || (apiClientConfig.applicationEnvironment === Environment.test);

  const disabledConditionToInvest = !profileData.isVerified
      || project.isReadonly
      || (money.amount < minInvestmentLimit)
      || (money.amount > maxInvestment.amount)
      || (money.amount + investmentFee.amount > profileData.balance.amount)
      || loading
      || (maxInvestment.amount === 0) || (isNoQualified && !isComplyRestriction) || (isNoQualified && !isUnderstandRisks);

  const handleSubmitToInvestment = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    try {
      if (devAndTest) {
        const manager = await createOrdersManager();
        const result = await manager.createOrder(project.uuid, money);
        navigate(RouteDictionary.ORDER_CONFIRMATION, {uuid: project.uuid, confirmationUuid: result});
      } else {
        const manager = await createInvestmentManager();
        const result = await manager.createInvestmentConfirmation(project.uuid, money);
        navigate(RouteDictionary.INVESTMENT_CONFIRMATION, {uuid: project.uuid, confirmationUuid: result.uuid});
      }
    } catch (error: any) {
      if (error.message === "Investment sum is greater than available limit") {
        setErrorMessage('Сумма инвестиции превышает допустимый лимит инвестирования для неквалифицированных инвесторов');
      } else {
        setErrorMessage("Произошла неизвестная ошибка");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="detailed-project-invest">
      <div
        className={cn("detailed-project-invest__container", resultIf(confirmationUuid !== undefined, "detailed-project-invest__container-none"))}>
        <div className="detailed-project-invest__progress">
          <div className="detailed-project-invest__progressAmounts">
            <Money money={project.accounting.investedSum}/>
            <Money money={project.accounting.loan}/>
          </div>
          <ProjectProgress
            percentage={calculateCollectedPercentage(project.accounting.investedSum, project.accounting.loan)}
            isPercent={false}
            hideValue
            className="detailed-project-invest__progressBar"
          />
          <div className="detailed-project-invest__progressLabels">
            <span>Собрано</span>
            <span>Цель</span>
          </div>
        </div>
        <div className="detailed-project-invest__main">
          <div className={
            money.amount > maxInvestment.amount ?
              "detailed-project-invest__money detailed-project-invest__money__error"
              :
              "detailed-project-invest__money"
          }>
            <div>
              Доступно для инвестирования:
            </div>
            <div>
              <Money money={maxInvestment}/>
            </div>
          </div>
          <div className={
            money.amount < minInvestmentLimit ?
              "detailed-project-invest__money detailed-project-invest__money--additional detailed-project-invest__money__error"
              :
              "detailed-project-invest__money detailed-project-invest__money--additional"
          }>
            <div>
              Минимальная инвестиция:
            </div>
            <div>
              <Money
                mode={MoneyMode.ADDITIONAL}
                money={new MoneyModel(minInvestmentLimit, "RUB")}
              />
            </div>
          </div>
          <div className={money.amount + investmentFee.amount > profileData.balance.amount ?
            "detailed-project-invest__money detailed-project-invest__money--additional detailed-project-invest__money__error"
            :
            "detailed-project-invest__money detailed-project-invest__money--additional"}>
            <div>
              Средств на балансе
            </div>
            <div>
              <Money mode={MoneyMode.ADDITIONAL} money={profileData.balance}/>
            </div>
          </div>
          <form className="detailed-project-invest__input" onSubmit={handleSubmitToInvestment}>
            <div className="detailed-project-invest__input-additional">
              Введите сумму для инвестирования:
            </div>
            <InputMoney
              money={money}
              onValueChanged={(value) => {
                setMoney(value);
                setErrorMessage('');
              }}
              onFocus={() => focused.current = true}
              onBlur={() => focused.current = false}
            />
            {errorMessage && isNoQualified && <div className={'detailed-project-invest__money__error'}>{errorMessage}</div>}
          </form>
        </div>
        <form className="detailed-project-invest__submit" onSubmit={handleSubmitToInvestment}>
          {(!profileData.isVerified || project.isReadonly) && <div className={"not-verified-info"}>
            <Information>
              {project.isReadonly
                ? 'Проект пока доступен только для ознакомления. Возможность проинвестировать скоро откроется.'
                : 'Чтобы начать инвестировать, вам необходимо пройти верификацию'}
            </Information>
          </div>
          }
          {!profileData.isVerified && <div className={'not-verified-info--mobile'}>
            <Information>
              Чтобы начать инвестировать, вам необходимо пройти верификацию
            </Information>
          </div>}
          <PrimaryButton expanded large color={
            (profileData.isVerified || !project.isReadonly)
              ? PrimaryButtonColor.GREEN
              : PrimaryButtonColor.GRAY}>
            <Button disabled={disabledConditionToInvest}
              type="submit">
              {devAndTest ? 'Подать заявку' : 'Инвестировать'}
            </Button>
          </PrimaryButton>
        </form>
        <div className="detailed-project-invest__risks">
          <label>
            <Checkbox checked={isUnderstandRisks} onChange={() => setIsUnderstandRisks((prev) => !prev)}>
              Понимаю риски, связанные с инвестированием в займы.
            </Checkbox>
          </label>
          <label>
            <Checkbox checked={isComplyRestriction} onChange={() => setIsComplyRestriction((prev) => !prev)}>
              Заверяю, что соблюдаю ограничение размера инвестирования, установленные ФЗ 259, ст7, п1 *.
            </Checkbox>
          </label>
          <Tooltip content={
            'Предупреждение о рисках!\n\n'
            + 'Инвестирование в займы является рисковым и может провести к полной или частичной потере средств инвестором.\n\n'
            + '* Согласно ФЗ 259, ст7, п1 для физического лица, не являющегося квалифицированным инвестором, '
            + 'установлен лимит инвестирования, сумма инвестирования не должна превышать 600т рублей в течении 1 года на всех инвестиционных платформах.'
          }>
            <button type="button" className="detailed-project-invest__risks-link">
              Ознакомиться с предупреждением о рисках
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
;

export default ProjectInvest;